import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  AuthorizationRequest, 
  CaseDocument, 
  CaseFollowUp, 
  ActivityLogEntry 
} from '@/lib/authorization/types';

interface UseCaseDetailOptions {
  caseId: string | null;
}

export function useCaseDetail({ caseId }: UseCaseDetailOptions) {
  const [caseData, setCaseData] = useState<AuthorizationRequest | null>(null);
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [followUps, setFollowUps] = useState<CaseFollowUp[]>([]);
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCaseData = useCallback(async () => {
    if (!caseId) {
      setCaseData(null);
      setDocuments([]);
      setFollowUps([]);
      setActivityLog([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch case with related data
      const { data: caseResult, error: caseError } = await supabase
        .from('authorization_requests')
        .select(`
          *,
          patient:patients(*),
          payer:payers(*),
          facility:facilities(*)
        `)
        .eq('id', caseId)
        .maybeSingle();

      if (caseError) throw caseError;
      setCaseData(caseResult as unknown as AuthorizationRequest);

      // Fetch documents
      const { data: docsResult, error: docsError } = await supabase
        .from('case_documents')
        .select('*')
        .eq('authorization_id', caseId)
        .order('created_at', { ascending: true });

      if (docsError) throw docsError;
      setDocuments(docsResult as CaseDocument[]);

      // Follow-ups (table removed — skip)
      setFollowUps([]);

      // Fetch activity log
      const { data: activityResult, error: activityError } = await supabase
        .from('authorization_activity_log')
        .select('*')
        .eq('authorization_id', caseId)
        .order('created_at', { ascending: false });

      if (activityError) throw activityError;
      setActivityLog(activityResult as ActivityLogEntry[]);

    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchCaseData();
  }, [fetchCaseData]);

  const updateCase = async (updates: Partial<AuthorizationRequest>) => {
    if (!caseId) return;

    const { error } = await supabase
      .from('authorization_requests')
      .update(updates)
      .eq('id', caseId);

    if (error) throw error;
    await fetchCaseData();
  };

  const addDocument = async (doc: Omit<CaseDocument, 'id' | 'created_at'>) => {
    const { error } = await supabase
      .from('case_documents')
      .insert([doc]);

    if (error) throw error;
    await fetchCaseData();
  };

  const updateDocument = async (docId: string, updates: Partial<CaseDocument>) => {
    const { error } = await supabase
      .from('case_documents')
      .update(updates)
      .eq('id', docId);

    if (error) throw error;
    await fetchCaseData();
  };

  const addFollowUp = async (_followUp: Omit<CaseFollowUp, 'id' | 'created_at'>) => {
    // Table removed — no-op
    console.warn('case_follow_ups table has been removed');
  };

  const completeFollowUp = async (_followUpId: string, _notes?: string) => {
    // Table removed — no-op
    console.warn('case_follow_ups table has been removed');
  };

  const logActivity = async (action: string, details?: Record<string, unknown>) => {
    if (!caseId) return;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('authorization_activity_log')
      .insert([{
        authorization_id: caseId,
        actor_id: user.id,
        actor_type: 'link_ops',
        action,
        details: details ? JSON.parse(JSON.stringify(details)) : null,
      }]);

    if (error) throw error;
    await fetchCaseData();
  };

  return {
    caseData,
    documents,
    followUps,
    activityLog,
    loading,
    error,
    refetch: fetchCaseData,
    updateCase,
    addDocument,
    updateDocument,
    addFollowUp,
    completeFollowUp,
    logActivity,
  };
}
